import React, { createContext, useContext, useCallback } from 'react';
import { clsx } from 'clsx';

// Form Context
interface FormContextType<T = Record<string, any>> {
  errors: Record<string, string>;
  values: T;
  setFieldValue: (name: string, value: any) => void;
  setFieldError: (name: string, error: string) => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

// Form Component
interface FormProps<T = Record<string, any>> {
  children: React.ReactNode;
  onSubmit?: (values: T) => void | Promise<void>;
  initialValues?: Partial<T>;
  className?: string;
}

const Form = <T extends Record<string, any> = Record<string, any>>({ 
  children, 
  onSubmit, 
  initialValues = {},
  className 
}: FormProps<T>) => {
  const [values, setValues] = React.useState<T>(initialValues as T);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const setFieldValue = useCallback((name: string, value: any) => {
    setValues(prev => ({ ...prev, [name]: value }));
    // Clear error when value changes
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const setFieldError = useCallback((name: string, error: string) => {
    setErrors(prev => ({ ...prev, [name]: error }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit?.(values);
  };

  return (
    <FormContext.Provider value={{ errors, values, setFieldValue, setFieldError }}>
      <form onSubmit={handleSubmit} className={className}>
        {children}
      </form>
    </FormContext.Provider>
  );
};

// Form Item Component
interface FormItemProps {
  children: React.ReactNode;
  label?: string;
  name?: string;
  required?: boolean;
  className?: string;
  help?: string;
}

const FormItem: React.FC<FormItemProps> = ({ 
  children, 
  label, 
  name,
  required = false,
  className,
  help 
}) => {
  const context = useContext(FormContext);
  const error = name && context?.errors[name];

  return (
    <div className={clsx('mb-4', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {React.Children.map(children, child => {
        if (React.isValidElement(child) && name) {
          return React.cloneElement(child, {
            ...child.props,
            name,
            value: context?.values[name] || '',
            error: error,
            onChange: (e: any, ...args: any[]) => {
              let value: any;
              
              // Handle different onChange signatures
              if (e && typeof e === 'object' && e.target) {
                // Standard HTML input event
                value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
              } else {
                // Custom component (like Select) that passes value directly
                value = e;
              }
              
              context?.setFieldValue(name, value);
              child.props.onChange?.(e, ...args);
            }
          });
        }
        return child;
      })}
      
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      
      {help && !error && (
        <p className="mt-1 text-sm text-gray-500">{help}</p>
      )}
    </div>
  );
};

// Hook to use form context
export const useForm = () => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useForm must be used within a Form component');
  }
  return context;
};

export { Form, FormItem };
export default Form; 